import { Cookie, ToggleLeft, ToggleRight } from 'lucide-react'
import { useState } from 'react'

function CookieToggle({ label, description, enabled, onChange, required = false }) {
  return (
    <div className="flex items-start justify-between gap-4 p-6 bg-card-dark border border-border-dark rounded-2xl">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-white font-bold">{label}</h3>
          {required && (
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 bg-white/5 px-2 py-0.5 rounded">
              Required
            </span>
          )}
        </div>
        <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
      </div>
      <button
        onClick={() => !required && onChange(!enabled)}
        disabled={required}
        className={`flex-shrink-0 transition-colors ${required ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        {enabled ? (
          <ToggleRight className="w-10 h-10 text-game-primary" />
        ) : (
          <ToggleLeft className="w-10 h-10 text-slate-500" />
        )}
      </button>
    </div>
  )
}

export function CookieSettingsPage() {
  const [settings, setSettings] = useState({
    essential: true,
    analytics: true,
    marketing: false,
    preferences: true,
  })

  const handleSave = () => {
    // Save cookie preferences
    localStorage.setItem('cookieSettings', JSON.stringify(settings))
    alert('Cookie preferences saved!')
  }

  const handleAcceptAll = () => {
    const allEnabled = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
    }
    setSettings(allEnabled)
    localStorage.setItem('cookieSettings', JSON.stringify(allEnabled))
  }

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
    }
    setSettings(essentialOnly)
    localStorage.setItem('cookieSettings', JSON.stringify(essentialOnly))
  }

  return (
    <div className="bg-background-dark min-h-screen pb-24 pt-40">
      <div className="container max-w-[800px] mx-auto px-6">
        <div className="flex items-center gap-3 mb-8">
          <Cookie className="w-10 h-10 text-game-primary" />
          <h1 className="text-white text-3xl md:text-4xl font-black">Cookie Settings</h1>
        </div>
        
        <p className="text-slate-400 mb-8 leading-relaxed">
          We use cookies and similar technologies to enhance your experience on Polytoria. 
          Manage your preferences below to control how we use cookies on your device.
        </p>

        <div className="space-y-4 mb-8">
          <CookieToggle
            label="Essential Cookies"
            description="These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as logging in or filling in forms."
            enabled={settings.essential}
            onChange={(val) => setSettings({ ...settings, essential: val })}
            required
          />

          <CookieToggle
            label="Analytics Cookies"
            description="These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most popular and see how visitors move around the site."
            enabled={settings.analytics}
            onChange={(val) => setSettings({ ...settings, analytics: val })}
          />

          <CookieToggle
            label="Marketing Cookies"
            description="These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant adverts on other sites."
            enabled={settings.marketing}
            onChange={(val) => setSettings({ ...settings, marketing: val })}
          />

          <CookieToggle
            label="Preference Cookies"
            description="These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages."
            enabled={settings.preferences}
            onChange={(val) => setSettings({ ...settings, preferences: val })}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleSave}
            className="bg-game-primary text-black font-black px-8 py-4 rounded-xl hover:scale-105 transition-transform"
          >
            Save Preferences
          </button>
          <button
            onClick={handleAcceptAll}
            className="bg-white/5 border border-border-dark text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors"
          >
            Accept All
          </button>
          <button
            onClick={handleRejectNonEssential}
            className="bg-white/5 border border-border-dark text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors"
          >
            Reject Non-Essential
          </button>
        </div>

        <div className="mt-16 space-y-8 text-slate-300">
          <section>
            <h2 className="text-white text-2xl font-bold mb-4">What Are Cookies?</h2>
            <p className="leading-relaxed">
              Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and provide information to the website owners.
            </p>
          </section>

          <section>
            <h2 className="text-white text-2xl font-bold mb-4">How We Use Cookies</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To keep you signed in to your account</li>
              <li>To remember your preferences and settings</li>
              <li>To understand how you use our platform</li>
              <li>To improve our services based on usage patterns</li>
              <li>To protect against fraud and abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-2xl font-bold mb-4">Third-Party Cookies</h2>
            <p className="leading-relaxed">
              Some cookies on our site are set by third parties such as analytics providers and advertising networks. These third parties may use cookies to collect information about your online activities across different websites.
            </p>
          </section>

          <section>
            <h2 className="text-white text-2xl font-bold mb-4">Managing Cookies in Your Browser</h2>
            <p className="leading-relaxed">
              Most web browsers allow you to control cookies through their settings. You can set your browser to block or delete cookies. However, blocking all cookies may affect your ability to use certain features of our platform.
            </p>
          </section>

          <section>
            <h2 className="text-white text-2xl font-bold mb-4">Contact Us</h2>
            <p className="leading-relaxed">
              If you have questions about our use of cookies, please contact us at{' '}
              <a href="mailto:privacy@polytoria.io" className="text-game-primary hover:underline">
                privacy@polytoria.io
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
